import { useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { Button } from 'reactstrap';

import {
    getMessageDate,
    getMessageFromParty,
    getMessageSubject,
    getMessageTo,
    getPartyInitials,
} from '../../../helpers';
import {
    collectThreadAncestors,
    getMessagePreview,
} from '../helpers/threadTree';
import ThreadMessage from './ThreadMessage';

const messageTimestamp = (message) => (
    getMessageDate(message) || message?.sent_at || message?.sentAt || ''
);

const formatThreadDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });
};

const formatHistorySent = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'long' });
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day} ${month} ${date.getFullYear()} ${hours}:${minutes}`;
};

const formatPartyLine = (party) => {
    if (party.name && party.email) {
        return `${party.name} <${party.email}>`;
    }

    return party.display || party.email || '';
};

const MessageSummary = ({ message, isOpen, t }) => {
    const party = getMessageFromParty(message);
    const sender = party.display || party.email || t('Unknown sender');
    const preview = getMessagePreview(message);
    const recipient = getMessageTo(message);

    return (
        <div className="case-mailbox-thread__header">
            <span className="case-mailbox-thread__avatar" aria-hidden="true">
                {getPartyInitials(party.name || party.display, party.email)}
            </span>
            <div className="case-mailbox-thread__summary">
                <span className="case-mailbox-thread__name text-truncate d-block">
                    {sender}
                </span>
                {isOpen ? (
                    recipient ? (
                        <div className="text-truncate">
                            <span className="case-mailbox-thread__to-label">{t('To')}: </span>
                            <span className="case-mailbox-thread__to-value">{recipient}</span>
                        </div>
                    ) : null
                ) : (
                    preview ? (
                        <div className="case-mailbox-thread__preview text-truncate">
                            {preview}
                        </div>
                    ) : null
                )}
            </div>
            <span className="case-mailbox-thread__date">
                {message?.has_attachments ? (
                    <i className="ri-attachment-2 case-mailbox-thread__attachment" aria-hidden="true" />
                ) : null}
                {formatThreadDate(messageTimestamp(message))}
            </span>
        </div>
    );
};

const HistoryItem = ({ node, t }) => {
    const party = getMessageFromParty(node.message);
    const sentAt = formatHistorySent(messageTimestamp(node.message));
    const recipient = getMessageTo(node.message);
    const subject = getMessageSubject(node.message);

    return (
        <div className="case-mailbox-thread__history-item">
            <div className="case-mailbox-thread__header">
                <span className="case-mailbox-thread__avatar" aria-hidden="true">
                    {getPartyInitials(party.name || party.display, party.email)}
                </span>
                <div className="case-mailbox-thread__summary">
                    <div>
                        <span className="case-mailbox-thread__meta-label">{t('From')}: </span>
                        <span className="case-mailbox-thread__meta-value">{formatPartyLine(party)}</span>
                    </div>
                    {sentAt ? (
                        <div>
                            <span className="case-mailbox-thread__meta-label">{t('Sent')}: </span>
                            <span className="case-mailbox-thread__meta-value">{sentAt}</span>
                        </div>
                    ) : null}
                    {recipient ? (
                        <div>
                            <span className="case-mailbox-thread__meta-label">{t('To')}: </span>
                            <span className="case-mailbox-thread__meta-value">{recipient}</span>
                        </div>
                    ) : null}
                    {subject ? (
                        <div>
                            <span className="case-mailbox-thread__meta-label">{t('Subject')}: </span>
                            <span className="case-mailbox-thread__meta-value">{subject}</span>
                        </div>
                    ) : null}
                </div>
                <span className="case-mailbox-thread__date">
                    {formatThreadDate(messageTimestamp(node.message))}
                </span>
            </div>
            <div className="case-mailbox-thread__history-body">
                <ThreadMessage message={node.message} embedded />
            </div>
        </div>
    );
};

const ThreadNode = ({ node, isOpen, t }) => {
    const [showHistory, setShowHistory] = useState(false);
    const ancestors = collectThreadAncestors(node);

    return (
        <Accordion.Item eventKey={node.key}>
            <Accordion.Header>
                <MessageSummary message={node.message} isOpen={isOpen} t={t} />
            </Accordion.Header>
            <Accordion.Body>
                <ThreadMessage message={node.message} embedded />
                {ancestors.length ? (
                    <div className="case-mailbox-thread__history-wrap">
                        <Button
                            size="sm"
                            type="button"
                            color="link"
                            className={`font-size-20 case-mailbox-thread__history-toggle${showHistory ? ' is-open' : ''}`}
                            aria-expanded={showHistory}
                            aria-label={t('Show earlier messages')}
                            onClick={() => setShowHistory((open) => !open)}
                        >
                            <i className="ri-arrow-down-s-line" aria-hidden="true" />
                        </Button>
                        {showHistory ? (
                            <div className="case-mailbox-thread__history">
                                {ancestors.map((ancestor) => (
                                    <HistoryItem key={ancestor.key} node={ancestor} t={t} />
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default ThreadNode;
